import { Text, View } from 'react-native'
import {
  hasSessionAnalysisMetrics,
  noteRangeLabel,
} from '@/src/features/statistics/utils/session-analysis-metrics'
import { backendSessionQueryOptions } from '@/src/queries/session-queries'
import { colors } from '@/src/theme'
import { body3, headline3 } from '@/src/theme/typography'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

interface VoiceAssessmentSessionAnalysisKpiCardProps {
  readonly label: string
  readonly value: string
  readonly className?: string
}

function VoiceAssessmentSessionAnalysisKpiCard({
  label,
  value,
  className,
}: VoiceAssessmentSessionAnalysisKpiCardProps) {
  return (
    <View
      className={`gap-1 rounded-lg bg-secondary700 px-4 py-3 ${className ?? ''}`}
    >
      <Text style={[body3, { color: colors.textBody }]}>{label}</Text>
      <Text style={[headline3, { color: colors.textHeader }]}>{value}</Text>
    </View>
  )
}

export interface VoiceAssessmentSessionAnalysisCardsLoaderProps {
  readonly sessionId: number
}

export function VoiceAssessmentSessionAnalysisCardsLoader({
  sessionId,
}: VoiceAssessmentSessionAnalysisCardsLoaderProps) {
  const { data: session } = useSuspenseQuery(
    backendSessionQueryOptions(sessionId),
  )

  const { t } = useTranslation(['statistics'])
  const placeholder = t('statistics:voice_assessment_placeholder_dash')

  if (!hasSessionAnalysisMetrics(session)) {
    return null
  }

  const singingScore =
    session.score == null ? placeholder : String(session.score)
  const comfortableRange =
    noteRangeLabel(session.comfortableMin, session.comfortableMax) ??
    placeholder
  const pitchRange =
    noteRangeLabel(session.pitchMin, session.pitchMax) ?? placeholder

  return (
    <View testID="voice-assessment-session-analysis-cards" className="gap-3">
      <VoiceAssessmentSessionAnalysisKpiCard
        label={t('statistics:voice_assessment_singing_score_label')}
        value={singingScore}
      />
      <View className="flex-row gap-3">
        <VoiceAssessmentSessionAnalysisKpiCard
          className="min-w-0 flex-1"
          label={t('statistics:voice_assessment_comfortable_range_label')}
          value={comfortableRange}
        />
        <VoiceAssessmentSessionAnalysisKpiCard
          className="min-w-0 flex-1"
          label={t('statistics:voice_assessment_pitch_range_label')}
          value={pitchRange}
        />
      </View>
    </View>
  )
}
